import { useMutation } from '@apollo/client';
import type { UpdateChipResellerInfoInput, UpdateResellerContactInfoInput } from '@/types/gql-op-types';
import { useToast } from '../../components/Toast/ToastContext';
import {
  rescueUpdateAccountInfoMutation,
  RescueUpdateAccountInfoData,
  RescueUpdateAccountInfoVariables,
  rescueUpdateResellerContactInfoMutation,
  RescueUpdateResellerContactInfoData,
  RescueUpdateResellerContactInfoVariables,
  rescueUpdateHouseholdNameMutation,
  RescueUpdateHouseholdNameData,
  RescueUpdateHouseholdNameVariables,
} from '../../lib/accountQueries';
import { AccountSection, trackAccountSectionSaved, trackAccountSectionSaveFailed } from '../../analytics';

/**
 * Shared per-section save for the account edit modals: runs the
 * updateChipResellerInfo mutation, toasts the outcome, and fires analytics.
 * The mutation response re-normalizes the ChipReseller cache entry, so the
 * read-only page updates without a refetch.
 *
 * Returns true on success so callers can close their modal.
 */
export function useAccountSave(section: AccountSection) {
  const { showToast } = useToast();
  const [mutate, { loading: saving }] = useMutation<RescueUpdateAccountInfoData, RescueUpdateAccountInfoVariables>(
    rescueUpdateAccountInfoMutation,
    { refetchQueries: ['rescueAccountInfo'] },
  );

  async function save(input: UpdateChipResellerInfoInput, successMessage: string): Promise<boolean> {
    try {
      await mutate({ variables: { input } });
      trackAccountSectionSaved(section);
      showToast(successMessage);
      return true;
    } catch (err) {
      trackAccountSectionSaveFailed(section);
      const detail = err instanceof Error ? err.message : 'Unknown error';
      showToast(`Failed to save: ${detail}`);
      return false;
    }
  }

  return { save, saving };
}

/**
 * Save for the Primary Contact section (name / phone / email) via the existing
 * `updateResellerContactInfo` mutation, whose fuller `ChipContactInfoInput`
 * persists name + email with no backend change. Same toast + analytics shape
 * as `useAccountSave`.
 */
export function useResellerContactSave() {
  const { showToast } = useToast();
  const [mutate, { loading: saving }] = useMutation<
    RescueUpdateResellerContactInfoData,
    RescueUpdateResellerContactInfoVariables
  >(rescueUpdateResellerContactInfoMutation, { refetchQueries: ['rescueAccountInfo'] });

  async function save(input: UpdateResellerContactInfoInput, successMessage: string): Promise<boolean> {
    try {
      await mutate({ variables: { input } });
      trackAccountSectionSaved('primary_contact');
      showToast(successMessage);
      return true;
    } catch (err) {
      trackAccountSectionSaveFailed('primary_contact');
      const detail = err instanceof Error ? err.message : 'Unknown error';
      showToast(`Failed to save: ${detail}`);
      return false;
    }
  }

  return { save, saving };
}

/**
 * Save for the Organization section. Keeps the org name in sync across records:
 * renames the reseller (name/type/website via `updateChipResellerInfo`) AND the
 * rescue household (name via `adminUpdateHouseholdName`, which a household OWNER
 * is allowed to call). Both are existing mutations — no backend change. Only
 * the applicable ops run (reseller op when a reseller; household op when there's
 * a rescue household), and a single toast reflects the combined result.
 */
export function useOrganizationSave() {
  const { showToast } = useToast();
  const [updateReseller, { loading: resellerSaving }] = useMutation<
    RescueUpdateAccountInfoData,
    RescueUpdateAccountInfoVariables
  >(rescueUpdateAccountInfoMutation, { refetchQueries: ['rescueAccountInfo'] });
  const [updateHouseholdName, { loading: householdSaving }] = useMutation<
    RescueUpdateHouseholdNameData,
    RescueUpdateHouseholdNameVariables
  >(rescueUpdateHouseholdNameMutation, { refetchQueries: ['rescueAccountInfo'] });

  async function save(
    args: { resellerInput: UpdateChipResellerInfoInput | null; householdId: string | null; name: string },
    successMessage: string,
  ): Promise<boolean> {
    const ops: Promise<unknown>[] = [];
    if (args.resellerInput) {
      ops.push(updateReseller({ variables: { input: args.resellerInput } }));
    }
    if (args.householdId) {
      ops.push(updateHouseholdName({ variables: { householdId: args.householdId, name: args.name } }));
    }
    if (ops.length === 0) {
      return false;
    }
    try {
      await Promise.all(ops);
      trackAccountSectionSaved('organization');
      showToast(successMessage);
      return true;
    } catch (err) {
      trackAccountSectionSaveFailed('organization');
      const detail = err instanceof Error ? err.message : 'Unknown error';
      showToast(`Failed to save: ${detail}`);
      return false;
    }
  }

  return { save, saving: resellerSaving || householdSaving };
}
